import {
  dateOffsetValuesTranslated,
  _checkDeleted,
  _checkListDeleted,
  displayNameDescriptor,
  nameDescriptor,
} from 'pages/AutomationEngine/helpers';
import {
  namedToOptionDeleted,
  namedToOptionError,
  displayNameToOption,
  fullNameToOptionError,
  objectNameToOption,
} from 'services/helpers';
import { ASSIGNMENT_TYPES, SCHEDULE_TYPES } from './constants';
import type {
  AssignmentType,
  ScheduleActivityDetails,
  ScheduleActivityDetailsForAPI,
  ScheduleActivityDetailsFromAPI,
  TeamMemberLike,
  VariableFromAPI,
  WithDeleted,
} from './types';
import type {
  AddErrorMessage,
  MessageDictionary,
} from 'pages/AutomationEngine/messageDictionary';
import type { ScheduleActivityStep } from '../actionSectionTypes';
import type {
  DeletedOption,
  VariableOption,
} from 'pages/AutomationEngine/types';

const typesWithRole: AssignmentType[] = [
  ASSIGNMENT_TYPES.SA_TYPE_LAST_ACTIVE_TEAM_MEMBER_SPEC_ROLE,
  ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_ROLE,
  ASSIGNMENT_TYPES.SA_TYPE_SPEC_ROLE,
];

const apiVariableToOption = (variable: VariableFromAPI): VariableOption => {
  const { name, deleted, ...meta } = variable;

  return {
    label: name,
    value: name,
    deleted,
    meta: { name, ...meta, deleted },
  };
};

const apiTeamMemberOption = ({
  id,
  displayName,
  deleted,
}: WithDeleted<TeamMemberLike>): DeletedOption & { data: TeamMemberLike } => {
  return {
    label: displayName,
    value: id,
    deleted,
    data: { id, displayName },
  };
};

const forApi = (step: ScheduleActivityStep) => {
  // need to convert the variable and type if DATEDUETYPE_ON_OR_AROUND_VARIABLE
  const orgDetails = step.details;

  let scheduleData: ScheduleActivityDetailsForAPI['schedule'];

  switch (orgDetails.schedule.type) {
    case 'with_delay': {
      const { withDelay } = orgDetails.schedule;

      scheduleData = {
        type: orgDetails.schedule.type,
        withDelay: {
          ...withDelay,

          delayVariable: withDelay?.delayVariable?.value
            ? { name: withDelay.delayVariable.value }
            : null,
          timeOffsetVariable: withDelay?.timeOffsetVariable?.value
            ? { name: withDelay.timeOffsetVariable.value }
            : null,
        },
        onOrAroundDate: undefined,
        skipNonWorkingDays: orgDetails.schedule.skipNonWorkingDays ?? null,
      };
      break;
    }
    case 'on_or_around_date':
    case 'on_or_around_date_variable': {
      const { onOrAroundDate } = orgDetails.schedule;

      scheduleData = {
        type:
          orgDetails.schedule.type === SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE
            ? SCHEDULE_TYPES.ON_OR_AROUND_DATE
            : orgDetails.schedule.type,
        onOrAroundDate: {
          ...onOrAroundDate,
          dateOffsetVariable: onOrAroundDate?.dateOffsetVariable?.value
            ? { name: onOrAroundDate.dateOffsetVariable.value }
            : null,
          timeOffsetVariable: onOrAroundDate?.timeOffsetVariable?.value
            ? { name: onOrAroundDate.timeOffsetVariable?.value }
            : null,
          variable: onOrAroundDate?.variable?.value
            ? { name: onOrAroundDate.variable.value }
            : null,
        },
        withDelay: undefined,
        skipNonWorkingDays: orgDetails.schedule.skipNonWorkingDays ?? null,
      };
      break;
    }

    default:
      scheduleData = {
        type: orgDetails.schedule.type,
        onOrAroundDate: undefined,
        skipNonWorkingDays: orgDetails.schedule.skipNonWorkingDays ?? null,
        withDelay: undefined,
      };
      break;
  }

  const api: { details: ScheduleActivityDetailsForAPI } = {
    details: {
      activityTypeId: orgDetails.activityType?.value!,
      assignedTo: {
        assignmentType: orgDetails.assignedTo.assignmentType,
        employeeId: orgDetails.assignedTo.employee?.value || undefined,
        roleId: orgDetails.assignedTo.role?.value || undefined,
        fieldId: orgDetails.assignedTo.field?.value || undefined,
        variable: orgDetails.assignedTo.variable?.value
          ? { name: orgDetails.assignedTo.variable.value }
          : undefined,
        employeeIds: orgDetails.assignedTo.employees.map((e) => e.value),
      },
      schedule: scheduleData,
      notifications: orgDetails.notifications || [],
      note: orgDetails.note,
      htmlNote: orgDetails.htmlNote,
      associationConfigs: orgDetails.associationConfigs.map((config, i) => {
        return {
          customObjectId: config.customObject.value,
          associationSource: config.associationSource,
          relationshipFieldId: config.relationshipField?.value || null,
          automationVariableName: config.automationVariable?.value || null,
        };
      }),
    },
  };
  return api;
};

const forFlow = (
  apiObject: ScheduleActivityDetailsFromAPI,
  addErrorMessage: AddErrorMessage,
  messageDictionary: MessageDictionary
) => {
  const { activityType, assignedTo, schedule, ...rest } = apiObject;

  _checkDeleted({
    messageDictionary,
    addErrorMessage,
    objectToCheck: activityType,
    descriptorCallback: nameDescriptor,
    objectPath: 'activityType',
    objectErrorTrans: namedToOptionError,
  });

  if (
    assignedTo.assignmentType &&
    typesWithRole.includes(assignedTo.assignmentType)
  ) {
    _checkDeleted({
      messageDictionary,
      addErrorMessage,
      objectToCheck: assignedTo.role,
      descriptorCallback: nameDescriptor,
      itemName: messageDictionary.deletedRole,
      objectPath: 'assignedTo.role',
      objectErrorTrans: namedToOptionError,
    });
  }

  _checkDeleted({
    messageDictionary,
    addErrorMessage,
    objectToCheck: assignedTo.employee,
    descriptorCallback: displayNameDescriptor,
    objectPath: 'assignedTo.employee',
    objectErrorTrans: fullNameToOptionError,
  });

  if (
    assignedTo.assignmentType ===
    ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_TEAM_MEMBERS
  ) {
    _checkListDeleted({
      messageDictionary,
      addErrorMessage,
      parentObject: assignedTo.employees,
      descriptorCallback: displayNameDescriptor,
      itemName: messageDictionary.teamMember,
      objectPath: 'assignedTo.employees',
      objectErrorTrans: (o) => o,
      checkChildren: true,
    });
  }

  const activityTypeWithEnabled = namedToOptionDeleted(activityType);

  if (activityTypeWithEnabled && activityType) {
    activityTypeWithEnabled.enabled = activityType.enabled;
  }

  const flow: ScheduleActivityDetails = {
    ...rest,
    activityType: activityTypeWithEnabled,
    assignedTo: {
      ...assignedTo,
      employees: (assignedTo.employees || []).map(apiTeamMemberOption),
      employee: assignedTo.employee && apiTeamMemberOption(assignedTo.employee),
      role: assignedTo.role ? namedToOptionDeleted(assignedTo.role) : null,
      field: assignedTo.field
        ? { ...displayNameToOption(assignedTo.field), data: assignedTo.field }
        : null,
      variable: assignedTo?.variable
        ? apiVariableToOption(assignedTo?.variable)
        : null,
    },
    schedule: {
      ...schedule,
      type:
        schedule?.onOrAroundDate?.variable &&
        schedule.type === SCHEDULE_TYPES.ON_OR_AROUND_DATE
          ? SCHEDULE_TYPES.ON_OR_AROUND_DATE_VARIABLE
          : schedule.type,
      onOrAroundDate: schedule.onOrAroundDate
        ? {
            ...schedule.onOrAroundDate,
            variable: schedule.onOrAroundDate?.variable
              ? apiVariableToOption(schedule.onOrAroundDate.variable)
              : null,
            dateOffsetVariable: schedule?.onOrAroundDate?.dateOffsetVariable
              ? apiVariableToOption(
                  schedule?.onOrAroundDate?.dateOffsetVariable
                )
              : null,
            timeOffsetVariable: schedule?.onOrAroundDate?.timeOffsetVariable
              ? apiVariableToOption(
                  schedule?.onOrAroundDate?.timeOffsetVariable
                )
              : null,
          }
        : {
            dateOffset: dateOffsetValuesTranslated((str) => str)[0].value,
            fieldId: null,
            dateOffsetType: 'static',
            dateOffsetDays: null,
            dateOffsetVariable: null,
            timeOffset: null,
            time: null,
            timeOffsetType: 'static',
            timeOffsetValue: null,
            timeOffsetVariable: null,
            timeFieldId: null,
            variable: null,
          },
      withDelay: {
        ...schedule.withDelay,
        delayVariable: schedule?.withDelay?.delayVariable
          ? apiVariableToOption(schedule?.withDelay?.delayVariable)
          : null,
        timeOffsetVariable: schedule?.withDelay?.timeOffsetVariable
          ? apiVariableToOption(schedule?.withDelay?.timeOffsetVariable)
          : null,
      },
      skipNonWorkingDays: schedule.skipNonWorkingDays ?? null,
    },
    associationConfigs: apiObject.associationConfigs.map((config) => {
      if (config.relationshipField) {
        _checkDeleted({
          messageDictionary,
          addErrorMessage,
          objectToCheck: config.relationshipField,
          descriptorCallback: displayNameDescriptor,
          objectPath: 'relationshipField',
        });
      }

      if (config.automationVariable) {
        _checkDeleted({
          messageDictionary,
          addErrorMessage,
          objectToCheck: config.automationVariable,
          descriptorCallback: nameDescriptor,
          objectPath: 'automationVariable',
        });
      }

      const { customObject } = config;

      if (customObject) {
        _checkDeleted({
          messageDictionary,
          addErrorMessage,
          objectToCheck: customObject,
          descriptorCallback: nameDescriptor,
          objectPath: 'customObject',
        });
      }

      return {
        customObject: {
          ...objectNameToOption(customObject),
          deleted: customObject.deleted,
          data: customObject,
        },
        associationSource: config.associationSource,
        relationshipField: config.relationshipField
          ? {
              ...displayNameToOption(config.relationshipField),
              deleted: config.relationshipField.deleted,
              data: config.relationshipField,
            }
          : null,
        automationVariable: config.automationVariable
          ? apiVariableToOption(config.automationVariable)
          : null,
      };
    }),
  };

  return flow;
};
export const scheduleActivityDTO = {
  forApi,
  forFlow,
};
